import { useMemo } from 'react';
import { DonationRulesConfig } from '../types/config';

/** Fixed donation amounts (€). The "custom" key covers any other amount
 *  the user types in. */
export const DONATION_FIXED_AMOUNTS = [1, 5, 10, 20, 50] as const;
export type DonationFixedAmount = (typeof DONATION_FIXED_AMOUNTS)[number];
export type DonationAmount = DonationFixedAmount | 'custom';

export type OneTimeVariantIds = DonationRulesConfig['one_time_variant_ids'];

const EMPTY_ONE_TIME_VARIANTS: OneTimeVariantIds = {
  '1': null,
  '5': null,
  '10': null,
  '20': null,
  '50': null,
  custom: null,
};

export interface DonationConfig {
  recurringVariantId: string | null;
  oneTimeVariantIds: OneTimeVariantIds;
  hasRecurring: boolean;
  hasAnyOneTime: boolean;
  hasAny: boolean;
  /** Both recurring AND at least one one-time variant configured —
   *  i.e. the user has an actual choice between the two modes. */
  showToggle: boolean;
}

/** Derives the donation widget config from donation_rules.
 *
 *  `disabled` short-circuits everything (used in PrestaShop where extra
 *  line items aren't wired into the carrier-level cart yet). El kill switch
 *  del admin (donation_rules.enabled=false) tiene el mismo efecto.
 */
export function useDonationConfig(
  donationRules: DonationRulesConfig | undefined,
  opts?: { disabled?: boolean },
): DonationConfig {
  const disabled = Boolean(opts?.disabled) || donationRules?.enabled === false;
  const recurringSource = donationRules?.recurring_variant_id ?? null;
  const oneTimeSource = donationRules?.one_time_variant_ids;
  return useMemo(() => {
    const recurringVariantId = disabled ? null : recurringSource;
    const oneTimeVariantIds = disabled ? EMPTY_ONE_TIME_VARIANTS : (oneTimeSource ?? EMPTY_ONE_TIME_VARIANTS);
    const hasRecurring = Boolean(recurringVariantId);
    const hasAnyOneTime = Object.values(oneTimeVariantIds).some(Boolean);
    return {
      recurringVariantId,
      oneTimeVariantIds,
      hasRecurring,
      hasAnyOneTime,
      hasAny: hasRecurring || hasAnyOneTime,
      showToggle: hasRecurring && hasAnyOneTime,
    };
  }, [disabled, recurringSource, oneTimeSource]);
}

/** Returns the configured Shopify variant id for a given amount, or null
 *  if that amount has no variant set. */
export function getOneTimeVariantId(
  variants: OneTimeVariantIds | undefined | null,
  amount: DonationAmount,
): string | null {
  if (!variants) return null;
  const key = (amount === 'custom' ? 'custom' : String(amount)) as keyof OneTimeVariantIds;
  return variants[key] ?? null;
}
